import React, { FC, MutableRefObject } from 'react'
import { Popover } from '@contrast-security-inc/design-system-popovers'
import { ContrastProduct } from '../shared'
import { Text } from '@contrast-security-inc/design-system-typography'
import { css, cx } from '@emotion/css'
import { useIntl } from 'react-intl'
import { formatStyles } from '../react-intl-helpers'

const POPOVER_SIZE = {
  width: 480,
  padding: 24
}

export const ServerProductConfigPopover = ({
  product,
  isProductOn,
  propertyName,
  propertyLocation,
  referenceElement,
  onClose
}: {
  product: ContrastProduct
  isProductOn: boolean
  propertyName: string
  propertyLocation: string
  referenceElement: MutableRefObject<HTMLButtonElement | null>
  onClose: () => void
}) => {
  const { formatMessage } = useIntl()

  return (
    <Popover
      testId={`popover-${product}-server-config`}
      hasArrow
      isOpen
      width={POPOVER_SIZE.width}
      placement="bottom"
      referenceElement={referenceElement?.current}
      onClose={onClose}
    >
      <div
        className={css`
          padding: ${POPOVER_SIZE.padding}px;
        `}
      >
        <RowWrapper>
          <Text font="bodyStrong">
            {formatMessage({
              id:
                product === 'PROTECT'
                  ? 'title_serverProductConfig_protect'
                  : 'title_serverProductConfig_assess'
            })}
          </Text>
        </RowWrapper>

        <RowWrapper>
          <Text testId={`popover-${product}-server-config-detail`} font="body">
            {formatMessage(
              {
                id: getPopoverI18nContent(product, isProductOn)
              },
              { ...formatStyles.bold }
            )}
          </Text>
        </RowWrapper>

        <RowWrapper>
          <Text font="body">
            {formatMessage({ id: 'detail_serverProductConfig_location' })}
          </Text>
        </RowWrapper>

        <RowWrapper
          className={css`
            padding: 16px;
            background-color: rgb(0, 154, 219, 0.15);
            overflow-wrap: break-word;
          `}
        >
          <Text font="body">
            {formatMessage(
              {
                id: 'detail_serverProductConfig_propertyInfo'
              },
              {
                propertyName,
                location: propertyLocation
              }
            )}
          </Text>
        </RowWrapper>

        <RowWrapper>
          <Text font="body">
            {formatMessage(
              {
                id: 'detail_serverProductConfig_restartServer'
              },
              { ...formatStyles.bold }
            )}
          </Text>
        </RowWrapper>
      </div>
    </Popover>
  )
}

const RowWrapper: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className
}) => {
  return (
    <div
      className={cx(
        css`
          margin-bottom: 24px;
        `,
        className
      )}
    >
      {children}
    </div>
  )
}

function getPopoverI18nContent(
  product: ContrastProduct,
  isProductOn: boolean
): string {
  switch (product) {
    case 'PROTECT':
      return isProductOn
        ? 'detail_serverProductConfig_protectOn'
        : 'detail_serverProductConfig_protectOff'
    case 'ASSESS':
      return isProductOn
        ? 'detail_serverProductConfig_assessOn'
        : 'detail_serverProductConfig_assessOff'
  }
}
